import React from 'react';
import { Card, CardContent, Typography, Chip, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, Divider, Stack } from '@mui/material';
import { Delete as DeleteIcon, Business as BusinessIcon } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useGetCentersQuery } from '../../../../../services/ConfigurationService';
import { CenterType } from '../../../../../services/ConfigurationService/types/centerType';
import { useAppSelector, useAppDispatch } from '../../../../../app/store';
import { removeCenterFromContract } from '../../../../../services/ContractService/contractSlice';
import { AddCenterDialog } from '../../dialogs';

interface ContractCentersListProps {
  // No props needed - component is self-contained
}

export const ContractCentersList: React.FC<ContractCentersListProps> = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const company = searchParams.get('company') || '';
  
  // Get contract state from Redux
  const contract = useAppSelector((state) => state.contract.currentContract);
  const selectedCenterViews = contract?.centers || [];
  
  // Convert CenterView codes to array for easier lookup
  const selectedCenterCodes = selectedCenterViews.map(center => center.code);
  
  // Fetch available centers for the company
  const { 
    data: availableCenters = [], 
    isLoading, 
    error 
  } = useGetCentersQuery(
    { company }, 
    { skip: !company }
  );

  // Get selected center details by mapping CenterView to CenterType
  const selectedCenterDetails = availableCenters.filter(center => 
    selectedCenterCodes.includes(center.code)
  );

  const handleRemoveCenter = (centerCode: string) => {
    dispatch(removeCenterFromContract(centerCode));
  };

  const formatAddress = (center: CenterType) => {
    const { address } = center;
    const parts = [
      address.address1,
      address.city,
      address.state,
      address.zip
    ].filter(Boolean);
    return parts.join(', ');
  };

  if (selectedCenterDetails.length === 0) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <BusinessIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="grey.600">
            No Centers Added
          </Typography>
          <Typography variant="body2" color="grey.500" sx={{ mb: 2 }}>
            Add centers to define where this contract applies.
          </Typography>
          <AddCenterDialog />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent sx={{ p: 0 }}>
        <List disablePadding>
          {selectedCenterDetails.map((center, index) => (
            <React.Fragment key={center.id}>
              {index > 0 && <Divider />}
              <ListItem sx={{ px: 0.5, py: 0 }}>
                <ListItemText
                  primary={
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                      <Typography variant="subtitle2">
                        {center.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ({center.code})
                      </Typography>
                      <Chip
                        label={center.centerType}
                        size="small"
                        variant="outlined"
                      />
                      {center.isInactive && (
                        <Chip
                          label="Inactive"
                          color="warning"
                          size="small"
                        />
                      )}
                    </Stack>
                  }
                  secondary={
                    <Typography variant="body2" color="text.secondary">
                      {formatAddress(center)}
                    </Typography>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    onClick={() => handleRemoveCenter(center.code)}
                    color="error"
                    size="small"
                    sx={{ ml: 1 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};
